/** Adapter between the API team member (raw Prisma + _count.clients) and the UI `TeamMember`. */

import type { AssignedClient, CompensationType, Currency, LinkedAccount, TeamMember, TeamRole, TeamStatus } from "@/types";
import { toTask, type ApiTask } from "@/features/tasks/tasks.adapter";
import type { RoleName } from "@/lib/permissions";

export interface ApiTeamMember {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  role: TeamRole;
  compensationType: CompensationType;
  salary?: number | string | null;
  currency?: Currency;
  status: TeamStatus;
  userId?: string | null;
  linkedAccount?: LinkedAccount | null;
  user?: LinkedAccount | null;
  clients?: AssignedClient[] | null;
  assignedTasks?: ApiTask[] | null;
  _count?: { clients?: number } | null;
  temporaryPassword?: string;
  accountCreated?: boolean;
}

export interface TeamMemberInput {
  name: string;
  email: string;
  phone?: string;
  role: TeamRole;
  compensationType: CompensationType;
  salary: number;
  currency: Currency;
  status: TeamStatus;
  createLoginAccount?: boolean;
  accountEmail?: string;
  temporaryPassword?: string;
  accountRole?: RoleName;
  accountIsActive?: boolean;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

export function toTeamMember(api: ApiTeamMember): TeamMember {
  const account = api.linkedAccount ?? api.user ?? null;
  const assignedClients = api.clients ?? undefined;
  const taskClientNames = new Map(
    (api.assignedTasks ?? []).flatMap((task): [string, string][] =>
      task.client ? [[task.client.id, task.client.name]] : [],
    ),
  );
  return {
    id: api.id,
    name: api.name,
    email: api.email,
    phone: api.phone ?? undefined,
    role: api.role,
    compensation: api.compensationType,
    salary: toNumber(api.salary),
    currency: api.currency ?? "DZD",
    status: api.status,
    userId: api.userId ?? account?.id ?? undefined,
    account,
    clientsCount: api._count?.clients ?? 0,
    assignedClients,
    assignedTasks: api.assignedTasks?.map((task) => toTask(task, taskClientNames)),
  };
}

export function toTeamWriteBody(input: TeamMemberInput): Record<string, unknown> {
  return {
    name: input.name,
    email: input.email,
    ...(input.phone ? { phone: input.phone } : {}),
    role: input.role,
    compensationType: input.compensationType,
    salary: input.salary,
    currency: input.currency,
    status: input.status,
    ...(input.createLoginAccount ? { createLoginAccount: true } : {}),
    ...(input.accountEmail ? { accountEmail: input.accountEmail } : {}),
    ...(input.temporaryPassword ? { temporaryPassword: input.temporaryPassword } : {}),
    ...(input.accountRole ? { accountRole: input.accountRole } : {}),
    ...(input.accountIsActive !== undefined ? { accountIsActive: input.accountIsActive } : {}),
  };
}

export function toMockTeamMember(input: TeamMemberInput): TeamMember {
  return {
    id: `m_${crypto.randomUUID().slice(0, 8)}`,
    name: input.name,
    email: input.email,
    phone: input.phone,
    role: input.role,
    compensation: input.compensationType,
    salary: input.salary,
    currency: input.currency,
    status: input.status,
    clientsCount: 0,
    account: input.createLoginAccount
      ? {
          id: `u_${crypto.randomUUID().slice(0, 8)}`,
          email: input.accountEmail ?? input.email,
          name: input.name,
          phone: input.phone,
          isActive: input.accountIsActive ?? true,
          roles: input.accountRole
            ? [{ id: input.accountRole, name: input.accountRole, label: input.accountRole, description: null }]
            : [],
        }
      : null,
  };
}
